import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Trash2, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Print types — value is the backend enum; `pet` marks pet-only types.
export const PRINT_TYPES = [
  { value: "fingerprint", label: "Fingerprint", pet: false },
  { value: "handfoot", label: "Hand & Foot Print", pet: false },
  { value: "nose", label: "Nose Print", pet: true },
  { value: "paw", label: "Paw Print", pet: true },
  { value: "picture", label: "Photo Print", pet: false },
  { value: "signature", label: "Signature", pet: false },
];
export const PRINT_LABEL = Object.fromEntries(PRINT_TYPES.map((p) => [p.value, p.label]));
export const isPet = (pt) => PRINT_TYPES.find((p) => p.value === pt)?.pet === true;

// Capture targets per print type (matches the app's enums; codes are legacy).
const FINGERS = [
  ["01", "01 — Left Baby"], ["02", "02 — Left Ring"], ["03", "03 — Left Middle"], ["04", "04 — Left Index"], ["05", "05 — Left Thumb"],
  ["06", "06 — Right Thumb"], ["07", "07 — Right Index"], ["08", "08 — Right Middle"], ["09", "09 — Right Ring"], ["10", "10 — Right Baby"],
];
const HANDFOOT = [["left_hand", "Left Hand"], ["right_hand", "Right Hand"], ["left_foot", "Left Foot"], ["right_foot", "Right Foot"]];
const PAWS = [["front_left", "Front Left Paw"], ["front_right", "Front Right Paw"], ["back_left", "Back Left Paw"], ["back_right", "Back Right Paw"]];
const targetOptions = (pt) => (pt === "fingerprint" ? FINGERS : pt === "handfoot" ? HANDFOOT : pt === "paw" ? PAWS : []);
const targetLabel = (pt, key) => (targetOptions(pt).find(([k]) => k === key)?.[1] || key);

const emptyForm = () => ({
  case_number: "",
  first_name: "", middle_name: "", last_name: "",
  gender: "",
  date_of_birth: "", date_of_death: "", time_of_death: "", place_of_death: "",
  print_type: "fingerprint",
  notes: "",
});

// Dates are stored as ISO strings; <input type="date"> wants yyyy-mm-dd.
const toDateInput = (v) => (v ? String(v).slice(0, 10) : "");

const formFromRecord = (record) => ({
  case_number: record.case_number || "",
  first_name: record.subject?.first_name || "",
  middle_name: record.subject?.middle_name || "",
  last_name: record.subject?.last_name || "",
  gender: record.subject?.gender || "",
  date_of_birth: toDateInput(record.subject?.date_of_birth),
  date_of_death: toDateInput(record.subject?.date_of_death),
  time_of_death: record.subject?.time_of_death || "",
  place_of_death: record.subject?.place_of_death || "",
  print_type: record.print_type || "fingerprint",
  notes: record.notes || "",
});

/**
 * Create or edit a saved print (memorial record).
 *
 * Editing deliberately covers the decedent details only: the print type and the
 * captured images are locked, because changing either would leave the record
 * describing a print that was never taken. That matches the app's edit mode.
 *
 * @param {object|null} record  null → create mode; a record → edit mode.
 * @param {function} onSaved    called after a successful save.
 */
export default function CaseFormDialog({ open, onOpenChange, record = null, onSaved }) {
  const editMode = Boolean(record);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  // Pending captures: { targetKey, file, previewUrl }
  const [captures, setCaptures] = useState([]);
  const [targetKey, setTargetKey] = useState("");

  // Reset whenever the dialog opens so a previous edit never leaks into a new case.
  useEffect(() => {
    if (!open) return;
    setForm(record ? formFromRecord(record) : emptyForm());
    setCaptures([]);
    setTargetKey("");
  }, [open, record]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const needsTarget = targetOptions(form.print_type).length > 0;

  const onPrintTypeChange = (v) => { set("print_type", v); setTargetKey(""); setCaptures([]); };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (needsTarget && !targetKey) { toast.error("Choose which print this image is for"); e.target.value = ""; return; }
    setCaptures((c) => [...c, { targetKey: needsTarget ? targetKey : null, file, previewUrl: URL.createObjectURL(file) }]);
    e.target.value = "";
  };

  const subjectBody = () => ({
    subject_type: isPet(form.print_type) ? "pet" : "human",
    first_name: form.first_name.trim(),
    middle_name: form.middle_name,
    last_name: form.last_name.trim(),
    gender: form.gender,
    date_of_birth: form.date_of_birth,
    date_of_death: form.date_of_death,
    time_of_death: form.time_of_death,
    place_of_death: form.place_of_death,
  });

  const save = async () => {
    if (!form.case_number.trim()) return toast.error("Case number is required");
    if (!form.first_name.trim() || !form.last_name.trim()) return toast.error("First and last name are required");
    setSaving(true);
    try {
      if (editMode) {
        // Print type and captures are not editable — details only.
        const res = await axios.patch(`${API}/memorial-records/${record.record_id}`, {
          case_number: form.case_number.trim(),
          notes: form.notes,
          subject: subjectBody(),
        });
        toast.success("Case details updated");
        onOpenChange(false);
        onSaved?.(res.data);
        return;
      }

      // 1. Upload images (if any) in one multipart request.
      let urls = [];
      if (captures.length > 0) {
        const fd = new FormData();
        captures.forEach((c) => fd.append("images", c.file));
        const up = await axios.post(`${API}/memorial-records/upload`, fd);
        urls = up.data?.urls || [];
      }
      // 2. Build captures[] aligned to uploaded urls.
      const captureBody = captures.map((c, i) => ({
        target: {
          kind: form.print_type,
          ...(form.print_type === "fingerprint" ? { finger: c.targetKey } : {}),
          ...(["handfoot", "paw"].includes(form.print_type) ? { target: c.targetKey } : {}),
        },
        image_url: urls[i],
        method: "gallery",
        quality_passed: true,
        captured_at: new Date().toISOString(),
      }));
      // 3. Create the record. subject_type is DERIVED from print type.
      const res = await axios.post(`${API}/memorial-records`, {
        case_number: form.case_number.trim(),
        print_type: form.print_type,
        notes: form.notes,
        captures: captureBody,
        subject: subjectBody(),
      });
      toast.success("Saved print order created");
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editMode ? "Edit Case Details" : "New Saved Print"} {isPet(form.print_type) ? "(Pet)" : "(Human)"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Case Number *"><Input value={form.case_number} onChange={(e) => set("case_number", e.target.value)} /></Field>
          <Field label="Print Type *">
            <Select value={form.print_type} onValueChange={onPrintTypeChange} disabled={editMode}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{PRINT_TYPES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
            {editMode && (
              <p className="text-[11px] text-slate-400">Locked — the captured print belongs to this type.</p>
            )}
          </Field>
          <Field label="First Name *"><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} /></Field>
          <Field label="Middle Name"><Input value={form.middle_name} onChange={(e) => set("middle_name", e.target.value)} /></Field>
          <Field label="Last Name *"><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} /></Field>
          <Field label="Gender">
            <Select value={form.gender || undefined} onValueChange={(v) => set("gender", v)}>
              <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Date of Birth"><Input type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} /></Field>
          <Field label="Date of Death"><Input type="date" value={form.date_of_death} onChange={(e) => set("date_of_death", e.target.value)} /></Field>
          <Field label="Time of Death"><Input type="time" value={form.time_of_death} onChange={(e) => set("time_of_death", e.target.value)} /></Field>
          <Field label="Place of Death"><Input value={form.place_of_death} onChange={(e) => set("place_of_death", e.target.value)} /></Field>
          <div className="col-span-2">
            <Field label="Notes"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
          </div>
        </div>

        {/* Capture images + target selection. Create only — captures are appended
            from the app, never replaced from here. */}
        {!editMode && (
          <div className="border-t pt-3 mt-1 space-y-2">
            <Label className="text-sm font-semibold text-slate-700">Capture Images</Label>
            {form.print_type === "fingerprint" && (
              <img
                src="/finger-guide.png"
                alt="Finger numbering guide: left hand 1–5, right hand 6–10"
                className="w-full max-w-md rounded-lg border"
              />
            )}
            <div className="flex flex-wrap items-end gap-3">
              {needsTarget && (
                <div className="w-52">
                  <Label className="text-xs text-slate-600">{form.print_type === "fingerprint" ? "Finger" : "Print area"}</Label>
                  <Select value={targetKey || undefined} onValueChange={setTargetKey}>
                    <SelectTrigger><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>{targetOptions(form.print_type).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <label className="inline-flex items-center gap-2 cursor-pointer rounded-lg border px-3 h-10 text-sm text-slate-700 hover:bg-slate-50">
                <Upload className="w-4 h-4" /> Add image
                <input type="file" accept="image/*" className="hidden" onChange={onFile} />
              </label>
            </div>
            {captures.length > 0 && (
              <div className="flex flex-wrap gap-3 pt-1">
                {captures.map((c, i) => (
                  <div key={i} className="relative text-center">
                    <img src={c.previewUrl} alt="" className="w-16 h-16 object-cover rounded-lg border" />
                    <p className="text-[10px] text-slate-500 mt-0.5 max-w-[64px] truncate">{c.targetKey ? targetLabel(form.print_type, c.targetKey) : PRINT_LABEL[form.print_type]}</p>
                    <button onClick={() => setCaptures((cs) => cs.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 bg-white rounded-full text-red-500 border">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-slate-400">{form.print_type === "fingerprint" ? "Add one image per finger (up to 10)." : "Attach the captured print image(s)."}</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving}>
            {saving ? "Saving…" : editMode ? "Save Changes" : "Save Print"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
